/**
 * Embed session persistence & token transport helpers.
 *
 * The embed handoff token (OTT) is single-use and short-lived (~120s): once it
 * has been exchanged at /external/embed/verify it is burned server-side, so a
 * full page load (F5, window.open, iframe re-mount) can no longer re-verify it.
 * The minted session is therefore persisted to sessionStorage (per-tab, copied
 * to tabs opened via window.open) and restored session-first on boot.
 *
 * Transport: the token arrives in the URL FRAGMENT (#token=...) — fragments are
 * not sent to servers, so they cannot leak via Referer or access logs (RFC 6750
 * §2.3). A ?token= query fallback is kept for legacy links during rollout.
 *
 * Shared by the vendor (공급사) and buyer (발주기관) embed entry points — both
 * exchange the same OTT against the same endpoint, so the persistence rules
 * must not drift between them.
 */

import type { EmbedTokenVerifyResponseDto } from "@semo/api-contracts";

export const EMBED_SESSION_STORAGE_KEY = "semo.embed.session.v1";

// Anything beyond this is treated as a corrupt/forged expiresAt.
const MAX_SESSION_LIFETIME_MS = 24 * 60 * 60 * 1000;
// Conservative fallback when the access token's exp claim cannot be decoded.
const DEFAULT_SESSION_TTL_MS = 30 * 60 * 1000;

export interface IStoredEmbedSession {
  accessToken: string;
  expiresAt: number;
  verify: EmbedTokenVerifyResponseDto;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isStoredEmbedSession(value: unknown): value is IStoredEmbedSession {
  if (!isRecord(value)) return false;
  const candidate = value;
  return (
    typeof candidate.accessToken === "string" &&
    candidate.accessToken.length > 0 &&
    typeof candidate.expiresAt === "number" &&
    Number.isFinite(candidate.expiresAt) &&
    typeof candidate.verify === "object" &&
    candidate.verify !== null
  );
}

export function readStoredEmbedSession(): IStoredEmbedSession | null {
  try {
    const raw = window.sessionStorage.getItem(EMBED_SESSION_STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!isStoredEmbedSession(parsed)) {
      window.sessionStorage.removeItem(EMBED_SESSION_STORAGE_KEY);
      return null;
    }

    const now = Date.now();
    if (
      parsed.expiresAt <= now ||
      parsed.expiresAt > now + MAX_SESSION_LIFETIME_MS
    ) {
      window.sessionStorage.removeItem(EMBED_SESSION_STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    // sessionStorage unavailable (blocked third-party storage) or corrupt JSON
    return null;
  }
}

export function hasStoredEmbedSession(): boolean {
  return readStoredEmbedSession() !== null;
}

export function persistEmbedSession(
  verify: EmbedTokenVerifyResponseDto,
): IStoredEmbedSession | null {
  const accessToken = verify.accessToken;
  if (!accessToken) return null;

  // Never let a refresh token reach persistent storage (OAuth Browser-Based
  // Apps BCP §6.1) — drop it even if the backend's escape hatch returned one.
  const safeVerify: EmbedTokenVerifyResponseDto = { ...verify };
  delete safeVerify.refreshToken;

  const session: IStoredEmbedSession = {
    accessToken,
    expiresAt:
      getJwtExpiryMs(accessToken) ?? Date.now() + DEFAULT_SESSION_TTL_MS,
    verify: safeVerify,
  };

  try {
    window.sessionStorage.setItem(
      EMBED_SESSION_STORAGE_KEY,
      JSON.stringify(session),
    );
  } catch {
    // Storage blocked — the session stays memory-only for this document.
  }

  return session;
}

export function clearEmbedSession(): void {
  try {
    window.sessionStorage.removeItem(EMBED_SESSION_STORAGE_KEY);
  } catch {
    // Storage blocked — nothing to clear.
  }
}

export function extractEmbedTokenFromLocation(
  location: Pick<Location, "hash" | "search"> = window.location,
): string | null {
  const fragment = location.hash.startsWith("#")
    ? location.hash.slice(1)
    : location.hash;
  const fromFragment = new URLSearchParams(fragment).get("token");
  if (fromFragment) return fromFragment;

  return new URLSearchParams(location.search).get("token");
}

export function removeEmbedTokenFromUrl(): void {
  const url = new URL(window.location.href);

  const fragmentParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  fragmentParams.delete("token");
  const fragment = fragmentParams.toString();
  url.hash = fragment ? `#${fragment}` : "";

  url.searchParams.delete("token");

  window.history.replaceState(window.history.state, "", url.toString());
}

/**
 * Best-effort decode of a JWT-shaped embed token's `sub` claim WITHOUT
 * signature verification. Only a routing hint: it detects that a persisted
 * session belongs to a different account than the token was minted for, so
 * the stale identity is never restored against someone else's token. It must
 * never be trusted for authentication — the server verifies the token.
 */
export function getEmbedTokenSubject(token: string): string | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  const sub = payload.sub;
  return typeof sub === "string" && sub.length > 0 ? sub : null;
}

function getJwtExpiryMs(jwt: string): number | null {
  const exp = decodeJwtPayload(jwt)?.exp;
  if (typeof exp === "number" && Number.isFinite(exp)) {
    return exp * 1000;
  }
  return null;
}

function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  const segments = jwt.split(".");
  const payloadSegment = segments[1];
  if (segments.length !== 3 || !payloadSegment) return null;

  try {
    const payload: unknown = JSON.parse(decodeBase64Url(payloadSegment));
    return isRecord(payload) ? payload : null;
  } catch {
    return null;
  }
}

function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  return window.atob(padded);
}
